import { FC } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { difference } from 'lodash'
import * as Yup from 'yup'
import { Field, Formik } from 'formik'

import { closeModal } from 'store/actions/modals'
import { createErrorNotification } from 'store/actions/notifications'
import { refreshCounter } from 'store/actions/refresh'
import { triggerResourcesRefresh } from 'store/actions/resources'
import {
  getAttributeName,
  getAttributeRankName,
  getUserName,
  getPositionName,
  getTagName,
  getTeamName,
  getProjectName,
  getDepartmentName
} from 'services/Utilities'
import { validateAttributes } from 'services/schedule'
import BaseModal from 'components/Modals/BaseModal'
import { Button } from 'components/Forms'
import { Form } from 'components/Forms'
import useApi from 'components/Hooks/useApi'
import { Card } from 'ui'

import { t } from 'translations'

// Types
type Props = {
  event: MData.Event & { projects: string }
  missingAttributes: boolean
  missingPosition: boolean
  missingRegion: boolean
  missingRegions: MData.UserWithFlags['flags']['shiftFlags']['shiftId']['reasons']['missingRegions']
  userId: string
  wrongDepartment: boolean
  wrongProject: boolean
  wrongTeam: boolean
}

type ValuesTypes = {
  attributesToUpdate: string[]
  positionToUpdate: boolean
  regionsToUpdate: string[]
  teamToUpdate: boolean
  projectToUpdate: boolean
  statusToUpdate: boolean
  departmentsToUpdate: string[]
}

const ScheduleFixUserEligibilityProblemsModal: FC<Props> = ({
  userId,
  missingRegion,
  missingPosition,
  wrongTeam,
  wrongDepartment,
  wrongProject,
  missingAttributes: isMissingAttributes,
  missingRegions,
  event
}): JSX.Element => {
  const dispatch = useDispatch()
  const { usersById, positionsById } = useSelector((state: MStore.RootState) => state.resources)

  // Query definition
  const { handleApi: updateUser } = useApi({ method: 'PUT', url: '/users/users' })

  const {
    attributes: userAttributes = [],
    positions: userPositions = [],
    projects: userProjects = [],
    teams: userTeams = [],
    regionalTags: userRegions = [],
    departments: userDepartments = []
  } = usersById[userId]

  // Get things to update
  const missingDepartments: string[] = difference(positionsById[event.positionId].departments, userDepartments)
  const attributes = validateAttributes(event).attributesToUpdate
  const missingAttributes = attributes?.missing.concat(attributes?.inferior) || []

  // Form validation
  const issuesShape = {
    attributesToUpdate: Yup.array().of(Yup.string()),
    positionToUpdate: Yup.boolean(),
    regionsToUpdate: Yup.array().of(Yup.string()),
    teamToUpdate: Yup.boolean(),
    projectToUpdate: Yup.boolean(),
    departmentsToUpdate: Yup.array().of(Yup.string()),
    statusToUpdate: Yup.boolean()
  }
  // Form initialization
  const initForm = () => ({
    attributesToUpdate: missingAttributes.map(({ attributeId }) => attributeId) || [],
    positionToUpdate: true,
    regionsToUpdate: missingRegions || [],
    teamToUpdate: true,
    projectToUpdate: true,
    statusToUpdate: true,
    departmentsToUpdate: missingDepartments || []
  })

  const onSubmit = async (values: ValuesTypes) => {
    const { attributesToUpdate, departmentsToUpdate, projectToUpdate, regionsToUpdate, positionToUpdate, teamToUpdate } = values
    // Add the attributes to update
    let newAttributes = [...userAttributes]
    if (attributesToUpdate.length && isMissingAttributes) {
      const missingAttributesToUpdate = attributes.missing?.filter(attribute => {
        return attributesToUpdate.includes(attribute.attributeId)
      })
      if (missingAttributesToUpdate.length) {
        newAttributes = [...newAttributes, ...missingAttributesToUpdate]
      }
      const inferiorAttributesToUpdate = attributes.inferior?.filter(attribute => {
        return attributesToUpdate.includes(attribute.attributeId)
      })
      for (const inferiorAttribute of inferiorAttributesToUpdate) {
        const attributeToUpdate = newAttributes.find(({ attributeId }) => inferiorAttribute.attributeId === attributeId)
        if (attributeToUpdate) attributeToUpdate.rank = inferiorAttribute.rank
      }
    }

    // Add the departments
    let departments = [...userDepartments]
    if (departmentsToUpdate.length && wrongDepartment) departments = [...departments, ...missingDepartments]

    // Add the position
    let positions = [...userPositions]
    if (positionToUpdate && missingPosition) positions = [...positions, event.positionId]

    // Add the team
    let teams = [...userTeams]
    if (teamToUpdate && wrongTeam) teams = event.team ? [...teams, event.team] : [...teams]

    // Add the project
    let projects = [...userProjects]
    if (projectToUpdate && wrongProject) projects = [...projects, event.projects]

    // Add the regions
    let regionalTags = [...userRegions]
    if (regionsToUpdate.length && missingRegion) regionalTags = [...regionalTags, ...missingRegions]

    // Update the user
    const { isError } = await updateUser({
      _id: userId,
      attributes: newAttributes,
      positions,
      teams,
      projects,
      regionalTags,
      departments
    })
    if (isError) return dispatch(createErrorNotification())

    // Trigger the refresh of the resources
    dispatch(refreshCounter('users'))
    triggerResourcesRefresh(dispatch, { resource: ['users'] })

    dispatch(closeModal())
  }

  return (
    <BaseModal hideButtons title={`manager.schedule.FIX_USER_ELIGIBILITY_PROBLEMS_MODAL`} titleVar={{ name: getUserName(userId) }}>
      <Formik validationSchema={Yup.object().shape(issuesShape)} enableReinitialize={true} initialValues={initForm()} onSubmit={onSubmit}>
        {({ isSubmitting, status, dirty, values, ...actions }) => {
          return (
            <Form resetForm={actions.resetForm} dirty={!!dirty && !isSubmitting}>
              {() => (
                <>
                  {/* Missing attributes */}
                  {isMissingAttributes ? (
                    <div className="mt-3">
                      <b>{t(`manager.schedule.FIX_USER_ELIGIBILITY_PROBLEMS_MODAL.ATTRIBUTES`)}</b>
                      <Card maxHeight="10rem" className="bg-light py-3 pl-3 my-3" role="group" aria-labelledby="checkbox-group">
                        {missingAttributes?.map(({ attributeId, rank }) => (
                          <label key={attributeId}>
                            <Field type="checkbox" name="attributesToUpdate" value={attributeId} />
                            <b>{getAttributeName(attributeId)}:</b> {getAttributeRankName(attributeId, rank)}
                          </label>
                        ))}
                      </Card>
                    </div>
                  ) : null}
                  {/* Missing position */}
                  {missingPosition ? (
                    <div className="mt-3">
                      <b>{t(`manager.schedule.FIX_USER_ELIGIBILITY_PROBLEMS_MODAL.POSITION`)}</b>
                      <Card maxHeight="10rem" className="bg-light py-3 pl-3 my-3">
                        <label>
                          <Field type="checkbox" name="positionToUpdate" />
                          {getPositionName(event.positionId)}
                        </label>
                      </Card>
                    </div>
                  ) : null}
                  {/* Missing regions */}
                  {missingRegion ? (
                    <div className="mt-3">
                      <b>{t(`manager.schedule.FIX_USER_ELIGIBILITY_PROBLEMS_MODAL.REGIONS`)}</b>
                      <Card maxHeight="10rem" className="bg-light py-3 pl-3 my-3">
                        {missingRegions?.map(regionId => (
                          <label key={regionId}>
                            <Field type="checkbox" name="regionsToUpdate" value={regionId} />
                            {getTagName(regionId)}
                          </label>
                        ))}
                      </Card>
                    </div>
                  ) : null}

                  {/* Wrong team */}
                  {event.team && wrongTeam ? (
                    <div className="mt-3">
                      <b>{t(`manager.schedule.FIX_USER_ELIGIBILITY_PROBLEMS_MODAL.TEAM`)}</b>
                      <Card maxHeight="10rem" className="bg-light py-3 pl-3 my-3">
                        <label>
                          <Field type="checkbox" name="teamToUpdate" />
                          {getTeamName(event.team)}
                        </label>
                      </Card>
                    </div>
                  ) : null}

                  {/* Wrong department */}
                  {wrongDepartment ? (
                    <div className="mt-3">
                      <b>{t(`manager.schedule.FIX_USER_ELIGIBILITY_PROBLEMS_MODAL.DEPARTMENT`)}</b>
                      <Card maxHeight="10rem" className="bg-light  py-3 pl-3 my-3">
                        {missingDepartments?.map(departmentId => (
                          <label key={departmentId}>
                            <Field type="checkbox" name="departmentsToUpdate" value={departmentId} />
                            {getDepartmentName(departmentId)}
                          </label>
                        ))}
                      </Card>
                    </div>
                  ) : null}

                  {/* Wrong project */}
                  {wrongProject ? (
                    <div className="mt-3">
                      <b>{t(`manager.schedule.FIX_USER_ELIGIBILITY_PROBLEMS_MODAL.PROJECT`)}</b>
                      <Card maxHeight="10rem" className="bg-light py-3 pl-3 my-3">
                        <label>
                          <Field type="checkbox" name="projectToUpdate" />
                          {getProjectName(event.projects)}
                        </label>
                      </Card>
                    </div>
                  ) : null}

                  {/* Submit & Cancel buttons */}
                  <div>
                    <Button className="btn btn-primary mr-2" type="submit" isSubmitting={isSubmitting} text={t(`actions.CONFIRM`)} />
                    <Button className="btn btn-outline-secondary" onClick={() => dispatch(closeModal())} text={t(`actions.CANCEL`)} />
                  </div>
                </>
              )}
            </Form>
          )
        }}
      </Formik>
    </BaseModal>
  )
}

export default ScheduleFixUserEligibilityProblemsModal
